import { Link } from "@tanstack/react-router";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTRPCClient } from "../../lib/api";
import { firebaseTokenSource } from "../../lib/auth-token";
import { useClients } from "../../lib/clients";
import { Button, ErrorText } from "../../ui";
import { MeetButton } from "../meet/MeetButton";
import { meetInvitees } from "../meet/model";
import { Composer } from "./Composer";
import { useMarkRead, useMessages } from "./data";
import { uploadChatImage } from "./images";
import { GroupView, PendingRow, type ListContext } from "./MessageList";
import { channelLabel, groupMessages, ordered, visiblePending, type Channel, type Member, type Pending } from "./model";

const NEAR_BOTTOM_PX = 80;

/**
 * One channel or DM: live messages, history on demand, the composer, and the read marker. The
 * inbox shows it too (WP-054), with its own way back and its own actions in the header.
 */
export function Conversation({
  workspaceId,
  channel,
  myUid,
  members,
  byUid,
  readAt,
  back,
  actions,
}: {
  workspaceId: string;
  channel: Channel;
  myUid: string;
  members: readonly Member[];
  byUid: ReadonlyMap<string, Member>;
  readAt: Date | null | undefined;
  /** In place of the phone's link back to the conversation list. */
  back?: ReactNode;
  actions?: ReactNode;
}) {
  const api = useTRPCClient();
  const { config, firebase } = useClients();
  const { state, loaded, loadingOlder, loadOlder, error } = useMessages(workspaceId, channel.id);
  const [pending, setPending] = useState<Pending[]>([]);
  const counter = useRef(0);
  useMarkRead(workspaceId, channel.id, myUid, channel.lastMessageAt, readAt);

  const messages = useMemo(() => ordered(state), [state]);
  const groups = useMemo(() => groupMessages(messages), [messages]);
  const shownPending = visiblePending(pending, state);
  const label = channelLabel(channel, myUid, byUid);
  const ctx: ListContext = { workspaceId, channelId: channel.id, myUid, members, byUid };
  const memberEmails = useMemo(() => meetInvitees(channel, members, myUid), [channel, members, myUid]);

  // Scrolling: stay pinned to the newest message unless the reader scrolled up; keep the view
  // still when an older page is prepended.
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const anchor = useRef<{ height: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el === null) return;
    if (anchor.current !== null) {
      el.scrollTop = el.scrollHeight - anchor.current.height + anchor.current.top;
      anchor.current = null;
    } else if (pinned.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, shownPending.length, loaded]);

  const older = useCallback(() => {
    const el = scroller.current;
    if (el !== null) anchor.current = { height: el.scrollHeight, top: el.scrollTop };
    void loadOlder();
  }, [loadOlder]);

  const send = useCallback(
    (body: string, files: readonly File[], retryKey?: string) => {
      const key = retryKey ?? `p${++counter.current}`;
      const item: Pending = { key, body, files, createdAt: new Date(), status: "sending", id: null };
      setPending((all) => [...all.filter((p) => p.key !== key), item]);
      pinned.current = true;
      const target = { workspaceId, channelId: channel.id };
      const getToken = firebaseTokenSource(firebase.auth);
      // Images go up first (WP-058); the post names them. Each send has its own promise
      // (concurrent sends must each settle their own row).
      Promise.all(files.map((file) => uploadChatImage(window.fetch.bind(window), config.apiUrl, getToken, target, file)))
        .then((images) => api.chat.post.mutate({ ...target, body, ...(images.length === 0 ? {} : { images }) }))
        .then(
          (res) => setPending((all) => all.map((p) => (p.key === key ? { ...p, id: res.id } : p))),
          () => setPending((all) => all.map((p) => (p.key === key ? { ...p, status: "failed" } : p))),
        );
    },
    [api, config.apiUrl, firebase.auth, workspaceId, channel.id],
  );

  return (
    <section aria-label={label} className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b border-border pb-2">
        {back === undefined ? (
          <Link
            to="/w/$workspaceId/chat"
            params={{ workspaceId }}
            className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-sm text-accent hover:bg-surface-2 md:hidden"
          >
            <span aria-hidden="true">←</span> Conversations
          </Link>
        ) : (
          back
        )}
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-text">{label}</h2>
        <MeetButton workspaceId={workspaceId} channelId={channel.id} label={label} memberEmails={memberEmails} />
        {actions}
      </header>

      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
        }}
        className="min-h-0 flex-1 overflow-y-auto py-2"
      >
        {loaded && state.hasOlder ? (
          <div className="flex justify-center py-2">
            <Button variant="ghost" onClick={older} disabled={loadingOlder}>
              {loadingOlder ? "Loading…" : "Load earlier messages"}
            </Button>
          </div>
        ) : null}
        {loaded && !state.hasOlder && messages.length > 0 ? (
          <p className="py-2 text-center text-xs text-muted">This is the start of {label}.</p>
        ) : null}
        {!loaded ? <p className="py-4 text-center text-sm text-muted">Loading messages…</p> : null}
        {loaded && messages.length === 0 && shownPending.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted">No messages yet. Say hello.</p>
        ) : null}
        <ol role="log" aria-label={`Messages in ${label}`} className="flex flex-col">
          {groups.map((g) => (
            <GroupView key={g.key} group={g} ctx={ctx} />
          ))}
          {shownPending.map((p) => (
            <PendingRow key={p.key} item={p} onRetry={() => send(p.body, p.files, p.key)} />
          ))}
        </ol>
        {error === null ? null : <ErrorText>Messages could not be loaded: {error.message}</ErrorText>}
      </div>

      <div className="border-t border-border pt-2">
        <Composer label={`Message ${label}`} members={members} onSend={(body, files) => send(body, files)} images />
      </div>
    </section>
  );
}
